// src/lib/firebase.js
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, onIdTokenChanged, connectAuthEmulator, setPersistence, browserLocalPersistence } from "firebase/auth";

// 🔐 Extract env vars explicitly
const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
const authDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;

// 🛑 HARD GUARD — prevents duplicate-app crash
if (!apiKey || !authDomain) {
  console.error("Firebase env missing", {
    apiKeyPresent: !!apiKey,
    authDomainPresent: !!authDomain,
  });
  throw new Error(
    "Firebase config missing. Ensure REACT_APP_FIREBASE_API_KEY and REACT_APP_FIREBASE_AUTH_DOMAIN are defined at build time."
  );
}

const firebaseConfig = {
  apiKey,
  authDomain,
};

// ✅ Single, consistent initialization
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

const auth = getAuth(app);

// Make token persistence EXPLICIT (SDK default, stated here so a build/runtime can never
// silently resolve to session/in-memory persistence — which would drop the session on tab
// close/reopen and read as an unexpected "logout"). Non-fatal if it fails.
// Exported so the auth listeners can WAIT for persistence to be installed before treating a null
// user as "signed out". setPersistence() re-initialises the persistence layer and returns a promise;
// registering listeners while it is still in flight opens a window in which the SDK can report a
// null user that means "not restored yet", not "logged out". Everything downstream that clears
// stored auth must await this first — see the refresh-logout defect note in CRMAuthContext.
export const persistenceReady = setPersistence(auth, browserLocalPersistence).catch((e) => {
  console.warn("[firebase] setPersistence(browserLocalPersistence) failed:", e?.code || e);
});

// ✅ Firebase Auth Emulator (DEV / LOCAL ONLY)
if (
  process.env.REACT_APP_USE_FIREBASE_EMULATOR === "true" &&
  typeof window !== "undefined" &&
  window.location.hostname === "localhost"
) {
  connectAuthEmulator(auth, "http://localhost:9099", {
    disableWarnings: true,
  });
}

// Expose a promise that resolves when we know the initial token state
let _initialAuthReadyResolve;
export const initialAuthReady = new Promise(
  (res) => (_initialAuthReadyResolve = res)
);

let currentIdToken = null;

onIdTokenChanged(auth, async (user) => {
  try {
    // Do not act on a null user until persistence has actually been installed. Without this, the
    // line below could remove `crm_access_token` during the restoration window on a normal page
    // reload — which is exactly how a refresh turned into a forced re-login. A genuine sign-out
    // still clears, because after persistenceReady a null user really does mean signed out.
    if (!user) { try { await persistenceReady; } catch { /* fall through */ } }
    currentIdToken = user ? await user.getIdToken(false) : null;
    // Keep the legacy `crm_access_token` COMPAT CACHE in sync with the live Firebase session. This handler
    // fires on sign-in AND on every silent token refresh, so the many components that still read the cache
    // (dashboards, tickets, staff, integrations, scheduling…) transparently receive a fresh token instead of
    // the frozen login-time value — the root of the "401 after ~1h" storm. Firebase remains the PRIMARY
    // identity authority; this cache is only a mirror. Cleared on sign-out so no stale token can be reused.
    try {
      if (currentIdToken) localStorage.setItem('crm_access_token', currentIdToken);
      else localStorage.removeItem('crm_access_token');
    } catch { /* storage unavailable (private mode) — in-memory token + getFreshIdToken still work */ }
  } catch {
    currentIdToken = null;
  } finally {
    _initialAuthReadyResolve?.();
    _initialAuthReadyResolve = null;
  }
});

export { auth };

export function getCurrentIdToken() {
  return currentIdToken;
}

// DR-013: fresh, non-forced token. getIdToken(false) transparently refreshes when within ~5 min of
// expiry, so an awake tab never sends a stale token. Returns null when signed out.
export async function getFreshIdToken() {
  // Wait until Firebase has resolved the INITIAL auth state before reading currentUser. Otherwise a
  // caller that fires during the auth-restoration window (the MFA-status probe, the tenant by-brand
  // bootstrap, etc.) reads a momentarily-null currentUser and sends "Bearer null" → a false 401.
  try { await initialAuthReady; } catch { /* ignore */ }
  const u = auth.currentUser;
  if (!u) return null;
  try {
    currentIdToken = await u.getIdToken(false);
    return currentIdToken;
  } catch {
    return null;
  }
}

// DR-013: app-level SINGLE-FLIGHT forced refresh. All 401 retries, resume/online recovery, and WS
// reconnects funnel through this one in-flight promise so N concurrent triggers cause exactly ONE
// getIdToken(true) (no refresh stampede). Returns the fresh token or null.
let _forceRefreshInFlight = null;
export function forceRefreshIdToken() {
  if (_forceRefreshInFlight) return _forceRefreshInFlight;
  _forceRefreshInFlight = (async () => {
    try {
      const u = auth.currentUser;
      if (!u) return null;
      currentIdToken = await u.getIdToken(true);
      return currentIdToken;
    } catch {
      return null;
    } finally {
      _forceRefreshInFlight = null;
    }
  })();
  return _forceRefreshInFlight;
}
